import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { MobileNavigation } from '../components/MobileNavigation';
import { Button } from '../components/ui/button';
import { Users, Calendar, CheckCircle, Clock, GearSix, LockKey, SignOut, Scroll, MapPin, X, CreditCard, HourglassHigh, Wallet, QrCode, Camera, Image } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useNotifications } from '../hooks/useNotifications';
import { getApiBaseUrl } from '../utils/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Format date properly to avoid timezone issues
const formatSessionDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

// Format time for display
const formatTime = (timeString) => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const time = new Date();
  time.setHours(parseInt(hours), parseInt(minutes));
  return time.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [user, setUser] = useState(null);
  const [recentRegistrations, setRecentRegistrations] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [cancelling, setCancelling] = useState(null);
  const [players, setPlayers] = useState([]);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedPlayerQR, setSelectedPlayerQR] = useState(null);
  const [qrCode, setQRCode] = useState('');
  const [loadingQR, setLoadingQR] = useState(false);
  const { isSupported, isSubscribed, loading: notifLoading, subscribe, unsubscribe } = useNotifications();
  
  useEffect(() => {
    loadDashboardData();
  }, []);
  
  const loadDashboardData = async () => {
    try {
      // Get user info
      const userResponse = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (userResponse.ok) {
        const userData = await userResponse.json();
        setUser(userData);
      }
      
      // Get dashboard stats
      const statsResponse = await fetch(`${getApiBaseUrl()}/api/dashboard/stats`, {
        credentials: 'include'
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
      // Get credit balance
      const creditResponse = await fetch(`${getApiBaseUrl()}/api/payments/credits/balance`, {
        credentials: 'include'
      });
      if (creditResponse.ok) {
        const creditData = await creditResponse.json();
        setStats(prev => ({ ...prev, credit_balance: creditData.total_balance || 0 }));
      }
      
      // Get recent registrations
      const registrationsResponse = await fetch(`${getApiBaseUrl()}/api/registrations/my`, {
        credentials: 'include'
      });
      if (registrationsResponse.ok) {
        const regData = await registrationsResponse.json();
        setRecentRegistrations(regData.slice(0, 10)); // Show last 10
      }
      
      // Get players for QR codes
      const playersResponse = await fetch(`${getApiBaseUrl()}/api/players`, {
        credentials: 'include'
      });
      if (playersResponse.ok) {
        const playersData = await playersResponse.json();
        setPlayers(playersData);
      }
      
      // Get upcoming sessions
      const upcomingResponse = await fetch(`${getApiBaseUrl()}/api/registrations/parent/upcoming-sessions`, {
        credentials: 'include'
      });
      if (upcomingResponse.ok) {
        const upcomingData = await upcomingResponse.json();
        setUpcomingSessions(upcomingData);
      }
      
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelRegistration = async (registration) => {
    setCancelling(registration.registration_id);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/registrations/${registration.registration_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setRecentRegistrations(recentRegistrations.filter(r => r.registration_id !== registration.registration_id));
        toast.success(`Registration cancelled for ${registration.player?.name}`);
        // Reload dashboard data to update counts
        loadDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel registration');
      }
    } catch (error) {
      console.error('Cancel registration error:', error);
      toast.error('Failed to cancel registration');
    } finally {
      setCancelling(null);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };
  
  const showPlayerQR = async (player) => {
    setSelectedPlayerQR(player);
    setLoadingQR(true);
    setShowQRDialog(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/players/${player.player_id}/qr`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setQRCode(data.qr_code);
      } else {
        toast.error('Failed to load QR code');
      }
    } catch (error) {
      console.error('QR code error:', error);
      toast.error('Failed to load QR code');
    } finally {
      setLoadingQR(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="parent-dashboard">
      {/* Premium Header */}
      <div className="glass-effect sticky top-0 z-40">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Dashboard</p>
              <h1 className="text-2xl font-black uppercase tracking-tight">
                Welcome, {user?.name?.split(' ')[0]}
              </h1>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-primary/10" data-testid="settings-menu-btn">
                  <GearSix size={24} weight="duotone" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/change-password')} data-testid="change-password-menu-item">
                  <LockKey size={18} className="mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="logout-menu-item">
                  <SignOut size={18} className="mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-6 py-8 space-y-8 stagger-children">
        {/* Premium Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="stats-card group">
            <div className="icon-container mb-4 text-primary">
              <Users size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-primary mb-1">{stats.players_count || 0}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">My Players</p>
          </div>
          
          <div className="stats-card group">
            <div className="icon-container mb-4 text-blue-500">
              <Calendar size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-blue-500 mb-1">{stats.upcoming_sessions || 0}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Upcoming</p>
          </div>
          
          <div className="stats-card group">
            <div className="icon-container mb-4 text-green-500">
              <Wallet size={24} weight="duotone" />
            </div>
            <p className="text-4xl font-black text-green-500 mb-1">${(stats.credit_balance || 0).toFixed(0)}</p>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Credit</p>
          </div>
        </div>
        
        {/* Quick Actions - Premium Grid */}
        <div>
          <h2 className="text-lg font-heading mb-4">QUICK ACTIONS</h2>
          <div className="grid grid-cols-2 gap-4">
            <Button 
              onClick={() => navigate('/players')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-card hover:bg-primary/10 border border-border hover:border-primary/30 rounded-xl transition-all"
              variant="ghost"
              data-testid="manage-players-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Users size={22} weight="duotone" />
              </div>
              <span className="text-sm font-medium">Manage Players</span>
            </Button>
            
            <Button 
              onClick={() => navigate('/sessions')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-card hover:bg-blue-500/10 border border-border hover:border-blue-500/30 rounded-xl transition-all"
              variant="ghost"
              data-testid="browse-sessions-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                <Calendar size={22} weight="duotone" />
              </div>
              <span className="text-sm font-medium">Browse Sessions</span>
            </Button>
            
            <Button 
              onClick={() => navigate('/waivers')}
              className="h-24 flex flex-col items-center justify-center space-y-2 bg-card hover:bg-green-500/10 border border-border hover:border-green-500/30 rounded-xl transition-all col-span-2"
              variant="ghost"
              data-testid="program-rules-btn"
            >
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500">
                <Scroll size={22} weight="duotone" />
              </div>
              <span className="text-sm font-medium">Waivers & Rules</span>
            </Button>
          </div>
        </div>
        
        {/* Upcoming Sessions - Quick View */}
        {upcomingSessions.length > 0 && (
          <div>
            <h2 className="text-lg font-heading mb-4 flex items-center gap-2">
              <Calendar size={20} weight="duotone" className="text-primary" />
              UPCOMING SESSIONS
            </h2>
            <div className="space-y-3">
              {upcomingSessions.slice(0, 3).map((item) => (
                <Card key={item.registration_id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {item.session.title && (
                          <p className="text-sm font-bold text-primary mb-1">{item.session.title}</p>
                        )}
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar size={14} className="text-muted-foreground" />
                          <span className="text-sm font-medium">{formatSessionDate(item.session.date)}</span>
                          <span className="text-muted-foreground">•</span>
                          <Clock size={14} className="text-muted-foreground" />
                          <span className="text-sm">{formatTime(item.session.start_time)}</span>
                        </div>
                        {item.session.location && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin size={14} />
                            <span>{item.session.location.name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Users size={14} className="text-primary" />
                          <span className="text-sm text-primary font-medium">{item.player?.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            item.registration_type === 'comp' ? 'bg-purple-500/20 text-purple-400' :
                            item.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' :
                            'bg-yellow-500/20 text-yellow-500'
                          }`}>
                            {item.registration_type === 'comp' ? 'Comp' :
                             item.payment_status === 'paid' ? 'Confirmed' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {upcomingSessions.length > 3 && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/sessions')}
                >
                  View All ({upcomingSessions.length}) Sessions
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Check-In QR Codes - Easy Access */}
        {players.length > 0 && (
          <div>
            <h2 className="text-lg font-heading mb-4 flex items-center gap-2">
              <QrCode size={20} weight="duotone" className="text-primary" />
              CHECK-IN QR CODES
            </h2>
            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/30">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Tap to show QR code for quick session check-in
                </p>
                <div className="grid grid-cols-1 gap-3">
                  {players.map((player) => (
                    <button
                      key={player.player_id}
                      onClick={() => showPlayerQR(player)}
                      className="flex items-center justify-between p-4 bg-card border border-border rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-all group"
                      data-testid={`qr-btn-${player.player_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <QrCode size={24} weight="duotone" className="text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-base">{player.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {player.grade ? `Grade ${player.grade}` : `Age ${player.age}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-primary font-medium text-sm">
                        Show QR →
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Recent Registrations */}
        {recentRegistrations.length > 0 && (
          <div>
            <h2 className="text-lg font-heading mb-4">MY REGISTRATIONS</h2>
            <div className="space-y-4">
              {recentRegistrations.map((registration) => {
                const sessionDate = registration.session?.date;
                const isPastSession = sessionDate && new Date(sessionDate) < new Date(new Date().toDateString());
                const gradeGroup = registration.session?.grade_group;
                const gradeDisplay = gradeGroup === 'elementary' ? '3-5th' :
                                     gradeGroup === 'middle' ? '6-8th' :
                                     gradeGroup === 'high' ? '9-12th' : '';
                
                return (
                  <div key={registration.registration_id} className="p-5 bg-card rounded-2xl border border-border card-hover">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        {/* Player Name & Status */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-heading text-base">{registration.player?.name}</p>
                          <StatusBadge status={registration.status} />
                          {registration.payment_status === 'paid' ? (
                            <span className="flex items-center text-xs text-green-500">
                              <CheckCircle size={14} className="mr-1" /> Paid
                            </span>
                          ) : registration.payment_status === 'pending' ? (
                            <span className="flex items-center text-xs text-yellow-500">
                              <Clock size={14} className="mr-1" /> Payment Pending
                            </span>
                          ) : null}
                        </div>
                        
                        {/* Session Details */}
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} weight="duotone" />
                            <span>{formatSessionDate(registration.session?.date)}</span>
                            {gradeDisplay && (
                              <span className="text-xs px-1.5 py-0.5 bg-primary/10 rounded text-primary">
                                {gradeDisplay} Grade
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock size={14} weight="duotone" />
                            <span>{formatTime(registration.session?.start_time)} - {formatTime(registration.session?.end_time)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin size={14} weight="duotone" />
                            <span>
                              {registration.location?.name}
                              {registration.session?.court && ` - ${registration.session.court}`}
                            </span>
                          </div>
                          {registration.session?.price > 0 && (
                            <div className="flex items-center gap-2">
                              <CreditCard size={14} weight="duotone" />
                              <span>${registration.session.price.toFixed(2)}</span>
                            </div>
                          )}
                          {registration.status === 'waitlisted' && (
                            <div className="flex items-center gap-2 text-yellow-500">
                              <HourglassHigh size={14} weight="duotone" />
                              <span>You'll be notified when a spot opens</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Cancel Button */}
                      {!isPastSession && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 border-red-500/30 hover:bg-red-500/10 shrink-0"
                              disabled={cancelling === registration.registration_id}
                              data-testid={`cancel-registration-btn-${registration.registration_id}`}
                            >
                              {cancelling === registration.registration_id ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <>
                                  <X size={14} className="mr-1" />
                                  Cancel
                                </>
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-card border-border">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel Registration?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove {registration.player?.name} from the session on {formatSessionDate(registration.session?.date)}.
                                {registration.payment_status === 'paid' && (
                                  <span className="block mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-yellow-500 text-sm">
                                    <strong>Refund Policy:</strong> Cancellations 24+ hours before session receive automatic account credit. 
                                    Cancellations with less notice are reviewed by admin.
                                  </span>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep Registration</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleCancelRegistration(registration)}
                                className="bg-red-500 hover:bg-red-600"
                                data-testid={`confirm-cancel-registration-${registration.registration_id}`}
                              >
                                Yes, Cancel
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {recentRegistrations.length === 0 && (
          <Card className="bg-card border-border">
            <CardContent className="text-center py-12">
              <Calendar size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg mb-2">NO REGISTRATIONS YET</h3>
              <p className="text-muted-foreground mb-4">Start by adding your players and browsing available sessions.</p>
              <div className="space-y-2">
                <Button 
                  onClick={() => navigate('/players')}
                  className="w-full btn-primary"
                  data-testid="add-players-btn"
                >
                  Add Players
                </Button>
                <Button 
                  onClick={() => navigate('/sessions')}
                  variant="outline"
                  className="w-full"
                  data-testid="view-sessions-btn"
                >
                  View Sessions
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      <MobileNavigation userRole="parent" />
      
      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-heading">
              {selectedPlayerQR?.name?.toUpperCase()}'S CHECK-IN
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {loadingQR ? (
              <div className="w-64 h-64 flex items-center justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : qrCode ? (
              <>
                <div className="bg-white p-4 rounded-2xl shadow-lg">
                  <img src={qrCode} alt="Player QR Code" className="w-56 h-56" />
                </div>
                <p className="text-center text-sm text-muted-foreground mt-4 px-4">
                  Show this to staff at session check-in
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">Failed to load QR code</p>
            )}
          </div>
          <Button 
            onClick={() => setShowQRDialog(false)}
            className="w-full btn-primary"
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatsCard({ icon, title, value, subtitle, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-center space-x-3">
          <div className={color}>{icon}</div>
          <div className="flex-1">
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{title}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const styles = {
    registered: 'bg-green-500/20 text-green-400 border-green-500/30',
    waitlisted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  
  return (
    <span className={`px-2 py-1 text-xs rounded-full border ${styles[status] || styles.registered}`}>
      {status.toUpperCase()}
    </span>
  );
}